import React, { useEffect, useRef, useState } from "react";

const TWITCH_CHANNEL = "150k";

const LivePage: React.FC = () => {
  const twitchRef = useRef<HTMLDivElement>(null);
  const [isLive, setIsLive] = useState<boolean | null>(null);
  const [twitchReady, setTwitchReady] = useState(false);
  const [ytVideoId, setYtVideoId] = useState<string>("");

  // ── Fetch latest YouTube video ID from server ──────────────────────────────
  useEffect(() => {
    fetch("/api/yt-video")
      .then((r) => r.json())
      .then((d) => setYtVideoId(d.videoId || ""))
      .catch(() => {});
  }, []);

  // ── Load Twitch embed script ───────────────────────────────────────────────
  useEffect(() => {
    if (document.getElementById("twitch-embed-script")) {
      setTwitchReady(true);
      return;
    }
    const s = document.createElement("script");
    s.id = "twitch-embed-script";
    s.src = "https://embed.twitch.tv/embed/v1.js";
    s.async = true;
    s.onload = () => setTwitchReady(true);
    document.head.appendChild(s);
  }, []);

  // ── Mount Twitch player ────────────────────────────────────────────────────
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Twitch = (window as any).Twitch;
    if (!twitchReady || !Twitch || !twitchRef.current) return;
    twitchRef.current.innerHTML = "";
    const player = new Twitch.Player("twitch-embed-container", {
      channel: TWITCH_CHANNEL,
      width: "100%",
      height: "100%",
      parent: [window.location.hostname],
      autoplay: false,
      muted: false,
    });
    player.addEventListener(Twitch.Player.ONLINE, () => setIsLive(true));
    player.addEventListener(Twitch.Player.OFFLINE, () => setIsLive(false));
    const fallback = setTimeout(
      () => setIsLive((p) => (p === null ? false : p)),
      6000,
    );
    return () => clearTimeout(fallback);
  }, [twitchReady]);

  const liveDotClass =
    isLive === null
      ? "bg-gray-500 animate-pulse"
      : isLive
        ? "bg-green-500 animate-pulse"
        : "bg-red-600";
  const liveLabel =
    isLive === null ? "Checking…" : isLive ? "LIVE NOW" : "OFFLINE";
  const liveLabelClass =
    isLive === null
      ? "text-gray-400"
      : isLive
        ? "text-green-400"
        : "text-red-500";

  return (
    <div className="min-h-screen w-full">
      <div className="max-w-[1400px] mx-auto py-8 sm:py-10 px-4">
        <h1 className="text-2xl sm:text-4xl font-bold mb-6 sm:mb-8 text-white tracking-wider text-center">
          Live
        </h1>

        {/* 3-column grid: stacks to 1 col on mobile, 2 on lg, 3 on xl */}
        <div className="flex flex-wrap justify-center gap-6">
          {/* ══════════════════════════════════════
              COL 1 — TWITCH
          ══════════════════════════════════════ */}
          <div className="bg-[#23232b] rounded-xl shadow border border-[#35353f] flex flex-col overflow-hidden max-w-[500px] w-full">
            <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-[#35353f]">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-[#9147ff]"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
                </svg>
                <span className="text-base font-bold text-gray-100">
                  Twitch
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#18181e] border border-[#35353f]">
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${liveDotClass}`}
                />
                <span
                  className={`text-xs font-bold tracking-widest ${liveLabelClass}`}
                >
                  {liveLabel}
                </span>
              </div>
            </div>

            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <div
                id="twitch-embed-container"
                ref={twitchRef}
                className="absolute inset-0 w-full h-full"
              />
              {isLive !== true && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#18181e] z-10 gap-4">
                  {isLive === null ? (
                    <>
                      <div className="w-10 h-10 rounded-full border-2 border-[#9147ff] border-t-transparent animate-spin" />
                      <span className="text-gray-400 text-sm">
                        Connecting to Twitch…
                      </span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-14 h-14 text-[#9147ff] opacity-30"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
                      </svg>
                      <p className="text-gray-500 text-sm font-medium">
                        {TWITCH_CHANNEL} is currently offline
                      </p>
                      <a
                        href={`https://www.twitch.tv/${TWITCH_CHANNEL}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 px-4 py-2 rounded-lg text-xs font-bold tracking-widest text-white bg-[#9147ff] hover:bg-[#7c3aed] transition-colors"
                      >
                        FOLLOW ON TWITCH
                      </a>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="px-4 sm:px-5 py-3 border-t border-[#35353f] flex items-center justify-between">
              <span className="text-xs text-gray-500 font-mono">
                twitch.tv/{TWITCH_CHANNEL}
              </span>
              <a
                href={`https://www.twitch.tv/${TWITCH_CHANNEL}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-1.5 text-xs text-[#9147ff] hover:text-[#c084fc] font-semibold transition-colors"
              >
                Open Channel
                <svg
                  className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>
          </div>

          {/* ══════════════════════════════════════
              COL 3 — YOUTUBE
          ══════════════════════════════════════ */}
          <div className="bg-[#23232b] rounded-xl shadow border border-[#35353f] flex flex-col overflow-hidden lg:col-span-2 xl:col-span-1 max-w-[500px] w-full">
            <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-[#35353f]">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-[#ff0000]"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
                <span className="text-base font-bold text-gray-100">
                  YouTube
                </span>
              </div>
              <a
                href={`https://www.youtube.com/@150kbtw`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors font-mono"
              >
                @150kbtw
              </a>
            </div>

            {/* YouTube latest video embed via channel RSS feed — no API key needed */}
            {/* We embed the channel page directly as an iframe */}
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              {ytVideoId ? (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${ytVideoId}?rel=0&modestbranding=1`}
                  title="150kbtw YouTube"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#18181e] gap-4">
                  <div className="w-10 h-10 rounded-full border-2 border-[#ff0000] border-t-transparent animate-spin" />
                  <span className="text-gray-400 text-sm">
                    Loading latest video…
                  </span>
                </div>
              )}
            </div>
            <div className="px-4 sm:px-5 py-3 border-t border-[#35353f] flex items-center justify-between">
              <span className="text-xs text-gray-500 font-mono">
                youtube.com/@150kbtw
              </span>
              <a
                href="https://www.youtube.com/@150kbtw"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-1.5 text-xs text-[#ff0000] hover:text-[#ff4444] font-semibold transition-colors"
              >
                Open Channel
                <svg
                  className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivePage;
